<template>
  <simple-scrollable-modal>
    <template #title
      >Cambios realizados {{ !lastChanges ? '(sin aplicar)' : '' }}</template
    >
    <template #content>
      <div v-for="group of groupedItems" :key="group.id">
        <h4>{{ group.title }}</h4>
        <v-data-table
          :headers="headers"
          :items="group.items"
          :mobile-breakpoint="0"
          dense
          :item-class="getItemClass"
          hide-default-footer
          :items-per-page="-1"
          class="items-table elevation-0 v-data-table--striped v-data-table--orange mt-1 mb-4"
        >
          <template #item.menuId="{ item }"
            ><span class="grey--text">{{ item.menu.name }}</span></template
          >

          <template #item.price="{ item }">
            <div class="text-right" v-if="!item.priceVariants">
              <span class="old-price" v-if="item.price !== item.newPrice">
                {{ currency.symbol }}{{ item.price }}
              </span>
              <v-icon
                small
                class="icon greater"
                v-if="item.newPrice > item.price"
                >mdi-plus</v-icon
              >
              <v-icon
                small
                class="icon lower"
                v-else-if="item.newPrice < item.price"
                >mdi-minus</v-icon
              >
              <v-icon small class="icon equal" v-else>mdi-equal</v-icon>
              <span class="new-price">
                {{ currency.symbol }}{{ item.newPrice }}
              </span>
            </div>
            <div class="price-variants" v-else>
              <div
                class="price-variant d-flex align-center justify-space-between"
                v-for="p of item.priceVariants.optionItems"
                :key="p.id"
              >
                <span class="name grey--text">{{ p.title }}</span>
                <v-spacer />
                <div>
                  <span class="old-price" v-if="p.cost !== p.newCost">
                    {{ currency.symbol }}{{ p.cost }}
                  </span>
                  <v-icon small class="icon greater" v-if="p.newCost > p.cost"
                    >mdi-plus</v-icon
                  >
                  <v-icon
                    small
                    class="icon lower"
                    v-else-if="p.newCost < p.cost"
                    >mdi-minus</v-icon
                  >
                  <v-icon small class="icon equal" v-else>mdi-equal</v-icon>
                  <span class="new-price">
                    {{ currency.symbol }}{{ p.newCost }}
                  </span>
                </div>
              </div>
            </div>
          </template>

          <template #item.discountedPrice="{ item }">
            <div class="text-right" v-if="!item.priceVariants">
              <span v-if="item.discountedPrice === null" class="grey--text"
                >-</span
              >
              <div v-else>
                <span
                  class="old-price"
                  v-if="item.discountedPrice !== item.newDiscountedPrice"
                >
                  {{ currency.symbol }}{{ item.discountedPrice }}
                </span>
                <v-icon
                  small
                  class="icon greater"
                  v-if="item.newDiscountedPrice > item.discountedPrice"
                  >mdi-plus</v-icon
                >
                <v-icon
                  small
                  class="icon lower"
                  v-else-if="item.newDiscountedPrice < item.discountedPrice"
                  >mdi-minus</v-icon
                >
                <v-icon small class="icon equal" v-else>mdi-equal</v-icon>
                <span class="new-price">
                  {{ currency.symbol }}{{ item.newDiscountedPrice }}
                </span>
              </div>
            </div>
            <div class="price-variants" v-else>
              <div
                class="price-variant d-flex align-center justify-end"
                v-for="p of item.priceVariants.optionItems"
                :key="p.id"
              >
                <span v-if="p.discountedCost === null" class="grey--text"
                  >-</span
                >
                <div v-else>
                  <span class="old-price" v-if="p.discountedCost !== p.cost">
                    {{ currency.symbol }}{{ p.discountedCost }}
                  </span>
                  <v-icon
                    small
                    class="icon greater"
                    v-if="p.newDiscountedCost > p.discountedCost"
                    >mdi-plus</v-icon
                  >
                  <v-icon
                    small
                    class="icon lower"
                    v-else-if="p.newDiscountedCost < p.discountedCost"
                    >mdi-minus</v-icon
                  >
                  <v-icon small class="icon equal" v-else>mdi-equal</v-icon>
                  <span class="new-price">
                    {{ currency.symbol }}{{ p.newDiscountedCost }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </v-data-table>
      </div>
    </template>
    <template #action-bar>
      <div class="d-flex justify-end align-center tw-space-x-2">
        <template v-if="!lastChanges">
          <v-btn text @click="cancel" :disabled="loading">{{
            $t('cancel')
          }}</v-btn>
          <v-btn color="primary" @click="save" :loading="loading">{{
            $t('save')
          }}</v-btn>
        </template>
        <template v-else>
          <v-btn
            color="error"
            @click="undoPriceChanges(lastChanges)"
            :loading="loading"
          >
            <v-icon>mdi-undo</v-icon>
            Revertir
          </v-btn>
          <v-btn @click="cancel" :disabled="loading" color="success">{{
            $t('close')
          }}</v-btn>
        </template>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import SimpleScrollableModal from './base/SimpleScrollableModal.vue';
  import {
    IItemPriceChange,
    IItemResumedPriceChange,
  } from '../../interfaces/PriceChanges';
  import { IRestaurantSettings } from '../../api/services/restaurants.service';
  import { IMenu } from '../../interfaces/IMenu';
  import { parseNumInput } from '../../utils';
  import { vueData } from '../../VueData';
  import cloneDeep from 'lodash/cloneDeep';
  import ItemsService from '../../api/services/items.service';
  import { AlertType } from '../../interfaces/IAlert';
  import { showConfirmModal } from '../../lib/modals';

  @Component({
    components: { SimpleScrollableModal },
  })
  export default class PricesPreview extends Vue {
    @Prop() items: IItemResumedPriceChange[];
    @Prop() restaurantSettings: IRestaurantSettings;
    @Prop() menus: IMenu[];

    itemsService = new ItemsService();
    currency = vueData.currency;

    lastChanges: IItemPriceChange[] = null;

    loading = false;

    async created() {}

    async save() {
      this.loading = true;
      try {
        let changes = this.getProcessedChanged();
        changes = await this.itemsService.massPriceChanges(changes);

        vueData.addAlert(
          'Cambios guardados',
          AlertType.Success /*, 5000, {
          text: 'Revertir',
          cb: () => this.undoPriceChanges(changes),
        }*/
        );

        this.lastChanges = changes;
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
      } finally {
        this.loading = false;
      }
    }

    async undoPriceChanges(changes: IItemPriceChange[]) {
      this.loading = true;

      try {
        await this.itemsService.undoPriceChanges(changes);
        vueData.addAlert('Cambios revertidos', AlertType.Success);
        this.lastChanges = null;
      } catch (e) {
        vueData.addAlert('Falló la reversión', AlertType.Error, 5000, {
          text: 'Reintentar',
          cb: () => this.undoPriceChanges(changes),
        });
        console.error(e);
      } finally {
        this.loading = false;
      }
    }

    getProcessedChanged() {
      const changes: IItemPriceChange[] = [];

      for (const i of this.previewItems) {
        const change: IItemPriceChange = { id: i.id };

        if (!i.priceVariants) {
          change.price = i.price;
          change.newPrice = i.newPrice;
          change.discountedPrice = i.discountedPrice;
          change.newDiscountedPrice = i.newDiscountedPrice;
        } else {
          change.priceVariants = {
            id: i.priceVariants.id,
            optionItems: [],
          };
          for (const oI of i.priceVariants.optionItems) {
            change.priceVariants.optionItems.push({
              id: oI.id,
              cost: oI.cost,
              newCost: oI.newCost,
              discountedCost: oI.discountedCost,
              newDiscountedCost: oI.newDiscountedCost,
            });
          }
        }

        changes.push(change);
      }

      return changes;
    }

    get previewItems() {
      const items: IItemResumedPriceChange[] = cloneDeep(this.items);

      for (const i of items) {
        i.newPrice = parseNumInput(i.newPrice ?? i.price);
        i.newDiscountedPrice = parseNumInput(
          i.newDiscountedPrice ?? i.discountedPrice
        );
        if (i.priceVariants) {
          for (const oI of i.priceVariants.optionItems) {
            oI.newCost = parseNumInput(oI.newCost ?? oI.cost);
            oI.newDiscountedCost = parseNumInput(
              oI.newDiscountedCost ?? oI.discountedCost
            );
          }
        }
      }

      return items;
    }

    get groupedItems() {
      const groups = [],
        groupsById = {};

      for (const i of this.previewItems) {
        const key = i.category.name;

        let group = groupsById[key];
        if (!group) {
          group = groupsById[key] = {
            id: i.categoryId,
            title: i.category.name,
            items: [],
          };
          groups.push(group);
        }

        group.items.push(i);
      }

      return groups;
    }

    get headers() {
      const headers = [
        { text: 'Nombre', value: 'name' },
        { text: 'Precio', value: 'price', align: 'end' },
        { text: 'C/ descuento', value: 'discountedPrice', align: 'end' },
      ];

      if (this.activeMenuIds.length > 1)
        headers.unshift({ text: 'Menú', value: 'menuId' });

      return headers;
    }

    get activeMenuIds() {
      const menuIds = [];

      if (!this.restaurantSettings.multipleMenus) menuIds.push(0);
      else {
        for (let i = 1; i <= 3; i++) {
          const menuId = this.restaurantSettings[`menu${i}Id`] ?? 0;
          if (menuIds.indexOf(menuId) === -1) menuIds.push(menuId);
        }
      }

      return menuIds;
    }

    getItemClass(item: IItemResumedPriceChange) {
      let price: number, newPrice: number;

      if (!item.priceVariants) {
        if (
          (item.discountedPrice !== null && item.newDiscountedPrice === null) ||
          (item.discountedPrice === null && item.newPrice === null)
        )
          return undefined;
        price = item.discountedPrice ?? item.price;
        newPrice = item.newDiscountedPrice ?? item.newPrice;
      } else {
        let avgCost: number = 0,
          avgNewCost: number = 0;

        for (const oI of item.priceVariants.optionItems) {
          const cost = oI.cost ?? 0,
            hasDiscountedCost = oI.discountedCost !== null;
          avgCost += oI.discountedCost ?? cost;
          if (hasDiscountedCost)
            avgNewCost += parseNumInput(
              oI.newDiscountedCost ?? oI.discountedCost
            );
          else avgNewCost += parseNumInput(oI.newCost ?? cost);
        }

        price = avgCost;
        newPrice = avgNewCost;
      }

      if (newPrice > price) return 'bigger';
      else if (newPrice < price) return 'smaller';
      else return undefined;
    }

    async cancel() {
      if (!this.lastChanges) this.$emit('close');
      else {
        const confirmed = await showConfirmModal(
          this.$modal,
          '¿Verificó todos los cambios?',
          'Una vez que cierre esta ventana no podrá revertir los cambios.'
        );
        if (confirmed) this.$parent.$emit('saved');
      }
    }
  }
</script>

<style lang="scss" scoped>
  .items-table {
    ::v-deep tr {
      td {
        border-bottom: 0 !important;
      }

      &.smaller {
        background-color: #ffe5e5 !important;

        &:nth-of-type(odd) {
          background-color: #ffd4d4 !important;
        }
      }

      &.bigger {
        background-color: #e9ffe5 !important;

        &:nth-of-type(odd) {
          background-color: #d5ffcf !important;
        }
      }
    }
  }

  .price-variants {
    padding: 4px 0;

    .price-variant {
      height: 20px;

      .name {
      }

      .cost {
        padding-right: 4px;
      }
    }
  }

  .old-price {
    color: #868686;
    font-weight: 300;
  }

  .new-price {
  }

  .icon {
    background-color: #ddd;
    border-radius: 999px;
    opacity: 0.7;

    &.greater {
      background-color: #37d41e;
      color: #fff !important;
    }

    &.lower {
      background-color: #d41e1e;
      color: #fff !important;
    }

    &.equal {
      background-color: #1e70d4;
      color: #fff !important;
    }
  }
</style>
